/**
 * WebSocket Service
 * 
 * Provides real-time communication with the backend WebSocket system.
 * Handles connection management, message broadcasting, and error recovery.
 */

import { logger } from '../utils/logger'
import { API_BASE_URL } from '../utils/constants'

export interface WebSocketMessage {
  type: string
  [key: string]: any
}

export interface WebSocketConnection {
  conversationId: string
  websocket: WebSocket
  isConnected: boolean
  reconnectAttempts: number
  maxReconnectAttempts: number
}

export class WebSocketService {
  private connections: Map<string, WebSocketConnection> = new Map()
  private messageHandlers: Map<string, Set<(message: WebSocketMessage) => void>> = new Map()
  private baseUrl: string
  private authToken: string | null = null

  constructor() {
    // Use WebSocket URL from environment variable
    const apiBaseUrl = API_BASE_URL
    this.baseUrl = `${apiBaseUrl.replace('https://', 'wss://').replace('http://', 'ws://')}/ws`

    logger.info('🔌 WebSocket service initialized', { baseUrl: this.baseUrl })
  }

  /**
   * Set authentication token for WebSocket connections
   */
  setAuthToken(token: string | null) {
    this.authToken = token
    logger.debug('🔑 WebSocket auth token updated')
  }

  /**
   * Get current authentication token from storage
   */
  private getCurrentToken(): string | null {
    try {
      // Try bgai-auth-storage first (React Query cache)
      const storedState = localStorage.getItem('bgai-auth-storage')
      if (storedState) {
        const parsed = JSON.parse(storedState)
        if (parsed.state?.token) {
          return parsed.state.token
        }
      }

      // Fallback to direct token storage
      const directToken = localStorage.getItem('token')
      if (directToken) {
        return directToken
      }

      return null
    } catch (error) {
      logger.error('❌ Failed to get current token', { error })
      return null
    }
  }

  /**
   * Connect to conversation WebSocket
   */
  async connectToConversation(conversationId: string): Promise<boolean> {
    try {
      // Close existing connection if any
      await this.disconnectFromConversation(conversationId)

      // Get the most current token before connecting
      const currentToken = this.getCurrentToken()
      if (!currentToken) {
        logger.warn('❌ No auth token available for WebSocket connection', { conversationId })
        return false
      }

      // Update auth token if it changed
      if (currentToken !== this.authToken) {
        this.setAuthToken(currentToken)
      }

      // Special handling for management dashboard
      let wsUrl: string
      if (conversationId === 'management_dashboard' || conversationId === 'conversation-management') {
        wsUrl = `${this.baseUrl}/conversation/management_dashboard?token=${currentToken}`
      } else {
        wsUrl = `${this.baseUrl}/conversation/${conversationId}?token=${currentToken}`
      }
      
      logger.debug('🔌 Connecting to WebSocket with fresh token', { conversationId, hasToken: !!currentToken })
      
      const websocket = new WebSocket(wsUrl)

      const connection: WebSocketConnection = {
        conversationId,
        websocket,
        isConnected: false,
        reconnectAttempts: 0,
        maxReconnectAttempts: 5
      }

      // Set up event handlers
      websocket.onopen = () => {
        connection.isConnected = true
        connection.reconnectAttempts = 0
        this.connections.set(conversationId, connection)
        
        logger.info('✅ WebSocket connected to conversation', { conversationId })
        
        // Send connection confirmation
        this.sendMessage(conversationId, { type: 'ping' })
      }

      websocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage
          this.handleMessage(conversationId, message)
        } catch (error) {
          logger.error('❌ Failed to parse WebSocket message', { error, data: event.data })
        }
      }

      websocket.onclose = (event) => {
        connection.isConnected = false
        logger.info('🔌 WebSocket disconnected from conversation', { 
          conversationId, 
          code: event.code, 
          reason: event.reason 
        })

        // Attempt reconnection if not intentional
        if (event.code !== 1000 && connection.reconnectAttempts < connection.maxReconnectAttempts) {
          setTimeout(() => {
            connection.reconnectAttempts++
            logger.info('🔄 Attempting WebSocket reconnection', { 
              conversationId, 
              attempt: connection.reconnectAttempts 
            })
            this.connectToConversation(conversationId)
          }, Math.pow(2, connection.reconnectAttempts) * 1000) // Exponential backoff
        } else {
          this.connections.delete(conversationId)
        }
      }

      websocket.onerror = (error) => {
        logger.error('❌ WebSocket error', { conversationId, error })
        connection.isConnected = false
      }

      return true
    } catch (error) {
      logger.error('❌ Failed to connect to conversation WebSocket', { conversationId, error })
      return false
    }
  }

  /**
   * Disconnect from conversation WebSocket
   */
  async disconnectFromConversation(conversationId: string): Promise<void> {
    const connection = this.connections.get(conversationId)
    if (connection) {
      connection.websocket.close(1000, 'Intentional disconnect')
      this.connections.delete(conversationId)
      this.messageHandlers.delete(conversationId)
      logger.info('🔌 Disconnected from conversation WebSocket', { conversationId })
    }
  }

  /**
   * Send message to conversation WebSocket
   */
  sendMessage(conversationId: string, message: WebSocketMessage): boolean {
    const connection = this.connections.get(conversationId)
    if (connection && connection.isConnected) {
      try {
        connection.websocket.send(JSON.stringify(message))
        return true
      } catch (error) {
        logger.error('❌ Failed to send WebSocket message', { conversationId, error })
        return false
      }
    }
    return false
  }

  /**
   * Subscribe to messages for a conversation
   */
  subscribe(conversationId: string, handler: (message: WebSocketMessage) => void): () => void {
    if (!this.messageHandlers.has(conversationId)) {
      this.messageHandlers.set(conversationId, new Set())
    }
    
    const handlers = this.messageHandlers.get(conversationId)!
    handlers.add(handler)
    
    logger.debug('📺 Subscribed to WebSocket messages', { conversationId })
    
    // Return unsubscribe function
    return () => {
      handlers.delete(handler)
      if (handlers.size === 0) {
        this.messageHandlers.delete(conversationId)
      }
      logger.debug('📺 Unsubscribed from WebSocket messages', { conversationId })
    }
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(conversationId: string, message: WebSocketMessage) {
    logger.debug('📨 WebSocket message received', { conversationId, type: message.type })
    
    const handlers = this.messageHandlers.get(conversationId)
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message)
        } catch (error) {
          logger.error('❌ Error in WebSocket message handler', { error })
        }
      })
    }
  }

  /**
   * Check if connected to conversation
   */
  isConnected(conversationId: string): boolean {
    const connection = this.connections.get(conversationId)
    return connection ? connection.isConnected : false
  }

  /**
   * Get connection status for all conversations
   */
  getConnectionStatus(): Record<string, boolean> {
    const status: Record<string, boolean> = {}
    this.connections.forEach((connection, conversationId) => {
      status[conversationId] = connection.isConnected
    })
    return status
  }

  /**
   * Disconnect all WebSocket connections
   */
  disconnectAll(): void {
    this.connections.forEach((_, conversationId) => {
      this.disconnectFromConversation(conversationId)
    })
    logger.info('🔌 Disconnected all WebSocket connections')
  }
}

// Export singleton instance
export const websocketService = new WebSocketService()

// Initialize auth token on first use rather than module load
// This prevents timing issues during login when localStorage might not be fully updated